import React, { useEffect, useState } from 'react';

import { Stack } from 'react-bootstrap';
import ReactImageFallback from 'react-image-fallback';
import { getDateTimeFormated } from '@/components/utils/dates/utils';
import './LomavisPostCard1.css';
import { LomavisPlatformIcon } from '../../../atoms/icons/LomavisPlatformIcon/LomavisPlatformIcon';
import { PLATFORM_LIST } from '@/config/constants';
import { translateComponent } from '@/utils/tolgee/translateComponent';
import PlaceholderImage from '../../../../assets/images/placeholders/images/placeholder-image-square.jpg';
import { LomavisShortenedNumber } from '../../utils/numbers/LomavisShortenedNumber';

interface ILomavisPostCard1AnalyticsProps {
  sum_comments: number | null;
  sum_engagement: number | null;
  sum_impressions: number | null;
  sum_reach: number | null;
  sum_reactions: number | null;
  sum_shares: number | null;
}

interface ILomavisPostCard1Props {
  /** The text to display inside the H1 heading */

  image: string;
  text: string;
  publicationDate: string;
  showProfileHead?: boolean;
  profile?: {
    name: string;
    imageUrl: string;
  };
  /** List of platforms where the post has been published */
  platforms?: Array<(typeof PLATFORM_LIST)[number]>;

  analytics?: ILomavisPostCard1AnalyticsProps;
}

/**
 * Atoms: LomavisPostCard1
 */
export const LomavisPostCard1: React.FC<ILomavisPostCard1Props> = ({
  image,
  text,
  publicationDate,
  platforms,
  analytics,
  showProfileHead,
  profile
}) => {
  const t = translateComponent('LomavisPostCard1');

  const [imgSrc, setImgSrc] = useState(image);

  const fallback = PlaceholderImage;

  useEffect(() => {
    setImgSrc(image);
  }, [image]);

  return (
    <Stack>
      <div className="lomavisPostCard pb-3 listItem pe-3">
        <div className="card min-h-200px mt-3 shadow">
          {showProfileHead && (
            <div className="px-2">
              <div className="d-flex align-items-center gap-2 py-2">
                <ReactImageFallback
                  src={profile.imageUrl}
                  fallbackImage={
                    <div className="d-flex w-100 h-100 align-items-center justify-content-center bg-secondary">
                      <i className="fa-regular fa-user fs-1 text-muted"></i>
                    </div>
                  }
                  alt="profile image"
                  className="w-50px h-50px rounded-circle object-fit-fill"
                />
                <div className="d-flex flex-column gap-1">
                  <span className="fw-bold">{profile?.name}</span>
                  <span className="text-muted fw-bold">
                    {' '}
                    {getDateTimeFormated(publicationDate)}
                  </span>
                </div>
              </div>
            </div>
          )}
          <div className="px-0 py-0 card-body d-flex flex-column justify-content-center align-items-center">
            {imgSrc && (
              <div className="image-container w-100">
                {/* Blurred background as actual DOM element */}
                <div
                  className="blur-background"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `url(${String(imgSrc || fallback)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(20px)',
                    transform: 'scale(1.1)',
                    zIndex: 1
                  }}
                />
                <img
                  src={imgSrc || fallback}
                  alt=""
                  className="foreground-img"
                  onError={() => setImgSrc(fallback)}
                  loading="lazy"
                />
              </div>
            )}
          </div>

          <div className="w-100 border-top p-3">
            <p className="px-2 text-dark mt-2 w-100 truncate-3-lines fs-6 fw-semibold text-gray-500">
              {text}
            </p>

            {platforms && platforms.length > 0 && (
              <div className="d-flex justify-content-end gap-2 mt-2 px-2">
                {platforms.map((platform, index) => (
                  <LomavisPlatformIcon key={index} platform={platform} />
                ))}
              </div>
            )}

            {analytics && (
              <div className="mt-3 pt-3 border-top">
                <h6 className="text-grey-800 mb-2">{t('postAnalytics')}</h6>
                <table className="table table-sm table-striped table-borderless border-0">
                  <tbody>
                    <tr>
                      <td className="px-3 fs-6 fw-semibold text-gray-700">
                        {t('impressions')}
                      </td>
                      <td className="text-end fw-bolder px-3">
                        {analytics.sum_impressions !== null &&
                        analytics.sum_impressions !== undefined ? (
                          <LomavisShortenedNumber
                            value={analytics.sum_impressions}
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 fs-6 fw-semibold text-gray-700">
                        {t('reach')}
                      </td>
                      <td className="text-end fw-bolder px-3">
                        {analytics.sum_reach !== null &&
                        analytics.sum_reach !== undefined ? (
                          <LomavisShortenedNumber value={analytics.sum_reach} />
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 fs-6 fw-semibold text-gray-700">
                        {t('reactions')}
                      </td>
                      <td className="text-end fw-bolder px-3">
                        {analytics.sum_reactions !== null &&
                        analytics.sum_reactions !== undefined ? (
                          <LomavisShortenedNumber
                            value={analytics.sum_reactions}
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 fs-6 fw-semibold text-gray-700">
                        {t('comments')}
                      </td>
                      <td className="text-end fw-bolder px-3">
                        {analytics.sum_comments !== null &&
                        analytics.sum_comments !== undefined ? (
                          <LomavisShortenedNumber
                            value={analytics.sum_comments}
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>

                    <tr>
                      <td className="px-3 fs-6 fw-semibold text-gray-700">
                        {t('shares')}
                      </td>
                      <td className="text-end fw-bolder px-3">
                        {analytics.sum_shares !== null &&
                        analytics.sum_shares !== undefined ? (
                          <LomavisShortenedNumber
                            value={analytics.sum_shares}
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 fs-6 fw-semibold text-gray-700">
                        {t('engagement')}
                      </td>
                      <td className="text-end fw-bolder px-3">
                        {analytics.sum_engagement !== null &&
                        analytics.sum_engagement !== undefined ? (
                          <LomavisShortenedNumber
                            value={analytics.sum_engagement}
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            <div className="w-100 d-flex justify-content-end fw-semibold text-gray-700  small">
              {getDateTimeFormated(publicationDate)}
            </div>
          </div>
        </div>
      </div>
    </Stack>
  );
};
